import { useRef, useState } from 'react';
import {
  NativeSyntheticEvent,
  Pressable,
  TextInput,
  TextInputChangeEventData,
  TextInputFocusEventData,
} from 'react-native';
import {
  FloatingField,
  IFloatingFieldProps,
  LabeledInput,
} from '@/common/components/Fields/FloatingField/FloatingField';
import { Icon } from '@/common/components/Icons/Icon';
import { Stack } from '@/common/components/Stack/Stack';

export function PasswordField({
  id,
  label,
  value,
  onChange,
  onBlur,
  className,
  hint,
  error,
}: IPasswordFieldProps) {
  const inputRef = useRef<TextInput | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [focused, setFocused] = useState(false);

  const isDirty = value.length > 0;
  return (
    <FloatingField
      error={error}
      focused={focused}
      className={className}
      hint={hint}
      onPress={() => inputRef.current?.focus()}
    >
      <LabeledInput
        label={label}
        active={focused || isDirty}
        ref={inputRef}
        inputProps={{
          id,
          secureTextEntry: !showPassword,
          value,
          onChange,
          onFocus: () => {
            setFocused(true);
          },
          onBlur: (evt) => {
            setFocused(false);
            onBlur?.(evt);
          },
          'aria-invalid': !!error,
        }}
      />
      <Stack
        horizontal
        gap="sm"
        className={styles.right}
        onMouseDown={(evt) => {
          evt.preventDefault();
        }}
      >
        <Pressable
          onPress={() => setShowPassword(!showPassword)}
          hitSlop={15}
          tabIndex={-1}
        >
          <Icon icon={showPassword ? 'eye-slash' : 'eye'} color="neutral20" />
        </Pressable>

        {!!error && <Icon icon="circle-exclamation" color="systemError" />}
      </Stack>
    </FloatingField>
  );
}

const styles = {
  right: 'pl-sm',
};

type FloatingFieldExtendable = Pick<IFloatingFieldProps, 'hint' | 'error'>;

export interface IPasswordFieldProps extends FloatingFieldExtendable {
  id: string;
  label: string;
  value: string;
  onChange: (evt: NativeSyntheticEvent<TextInputChangeEventData>) => void;
  onBlur?: (evt: NativeSyntheticEvent<TextInputFocusEventData>) => void;
  className?: string;
}
